/**
 * Dashboard "Next 30 days" card — team feedback P11 (Pedro 2026-10-10), visible to every user.
 *
 * Left: document & review dates falling due in the next 30 days (the same source as
 * Compliance → Expiring dates; overdue ones are counted with a link there). Right: EVERY
 * person's birthday in the next 30 days (rolling), each with the agreements they belong to
 * and those agreements' RMs.
 */
import { Anchor, Card, Group, ScrollArea, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconCalendarTime } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { complianceApi } from '../api/completeness';
import { useCurrentUser } from '../hooks/useAuth';
import { useExpiring } from '../hooks/useCompleteness';
import { EXPIRING_KIND_LABEL } from '../utils/expiringLabels';

const WINDOW_DAYS = 30;

/** 'YYYY-MM-DD' → '12 Oct' (local date, no timezone shift). */
function dayMonth(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function when(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

export function NextThirtyDaysCard() {
  // C-356 (2026-10-11): the firm-wide dates list is Admin / Compliance only. Other roles
  // see their own Named-RM clients here, and the overdue line leads to My follow-ups.
  // (Interim choice for the due list — question to Pedro, OPEN_ITEMS C-356.)
  const { user } = useCurrentUser();
  const firmWide = user?.role === 'admin' || user?.role === 'compliance';
  // within_months=1 → the server's 30-day window (30 days per month).
  const expQ = useExpiring(1, false, firmWide ? 'all' : 'mine', !!user);
  const bdQ = useQuery({
    queryKey: ['compliance', 'birthdays', WINDOW_DAYS],
    queryFn: () => complianceApi.birthdays(WINDOW_DAYS),
    staleTime: 60_000,
  });

  const records = expQ.data?.items ?? [];
  const due = records
    .flatMap((r) => r.dates
      .filter((d) => d.days_until_due >= 0 && d.days_until_due <= WINDOW_DAYS)
      .map((d) => ({ r, d })))
    .sort((a, b) => a.d.days_until_due - b.d.days_until_due);
  const overdue = records.reduce((n, r) => n + r.dates.filter((d) => d.days_until_due < 0).length, 0);
  const birthdays = Array.isArray(bdQ.data?.items) ? bdQ.data!.items : [];

  return (
    <Card withBorder shadow="sm">
      <Group justify="space-between" mb="sm">
        <Title order={4}>Next 30 days</Title>
        <IconCalendarTime size={24} />
      </Group>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
        <Stack gap={6}>
          <Text fw={600} size="sm">
            Documents &amp; reviews due{firmWide ? '' : ' · your clients'} ({due.length})
          </Text>
          <ScrollArea.Autosize mah={320}>
            <Stack gap={6}>
              {expQ.isLoading && <Text size="sm" c="dimmed">Loading…</Text>}
              {!expQ.isLoading && due.length === 0 && (
                <Text size="sm" c="dimmed">Nothing due in the next 30 days.</Text>
              )}
              {due.map(({ r, d }) => (
                <Group key={`${r.entity_id}-${d.kind}`} justify="space-between" wrap="nowrap" gap="xs">
                  <Anchor component={Link} to={d.detail_path} size="sm">
                    {r.label} — {EXPIRING_KIND_LABEL[d.kind] ?? d.kind}
                  </Anchor>
                  <Text size="xs" c={d.days_until_due <= 7 ? 'laranja.7' : 'dimmed'} style={{ whiteSpace: 'nowrap' }}>
                    {dayMonth(d.expires_on)} · {when(d.days_until_due)}
                  </Text>
                </Group>
              ))}
            </Stack>
          </ScrollArea.Autosize>
          {overdue > 0 && (
            <Anchor component={Link} to={firmWide ? '/compliance?tab=expiring' : '/my-follow-ups'}
              size="xs" c="red.7">
              {overdue} already overdue — see {firmWide ? 'Compliance' : 'My follow-ups'} › Expiring dates
            </Anchor>
          )}
        </Stack>

        <Stack gap={6}>
          <Text fw={600} size="sm">Birthdays ({birthdays.length})</Text>
          <ScrollArea.Autosize mah={320}>
            <Stack gap={8}>
              {bdQ.isLoading && <Text size="sm" c="dimmed">Loading…</Text>}
              {!bdQ.isLoading && birthdays.length === 0 && (
                <Text size="sm" c="dimmed">No birthdays in the next 30 days.</Text>
              )}
              {birthdays.map((b) => (
                <div key={b.person_id}>
                  <Group justify="space-between" wrap="nowrap" gap="xs">
                    <Anchor component={Link} to={b.detail_path} size="sm">{b.name}</Anchor>
                    <Text size="xs" c={b.days_until === 0 ? 'laranja.7' : 'dimmed'} style={{ whiteSpace: 'nowrap' }}>
                      {dayMonth(b.next_birthday)} · {when(b.days_until)} · turns {b.turns}
                    </Text>
                  </Group>
                  {b.agreements.length === 0 ? (
                    <Text size="xs" c="dimmed">No agreement</Text>
                  ) : (
                    b.agreements.map((a) => (
                      <Text key={a.agreement_id} size="xs" c="dimmed">
                        <Anchor component={Link} to={`/agreements/${a.agreement_id}`} size="xs">{a.label}</Anchor>
                        {a.status !== 'Active' ? ` (${a.status})` : ''} · RM: {a.rms.length ? a.rms.join(', ') : '—'}
                      </Text>
                    ))
                  )}
                </div>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      </SimpleGrid>
    </Card>
  );
}
